import Vue from "vue";
import SecureLS from "secure-ls";
import { getSubCompanyId } from "@/core/services/store.js";

const ls = new SecureLS({ encodingType: "aes", isCompression: true });
const dashboardMenuSyncState = Vue.observable({ version: 0 });

function bumpDashboardMenuVersion() {
    dashboardMenuSyncState.version += 1;
}

if (typeof window !== "undefined" && !window.__dashboardMenuSyncBound) {
    window.addEventListener("dashboard-menu-updated", bumpDashboardMenuVersion);
    window.addEventListener("app-language-changed", bumpDashboardMenuVersion);
    window.__dashboardMenuSyncBound = true;
}

// Permission helper
function hasPermission(permissionName) {
    const permissions = ls.get("permissions") || [];
    return permissions.some((p) => p.name === permissionName);
}

// Vue prototype extensions
Vue.prototype.$hasPermission = hasPermission;
Vue.prototype.$roleCount = function () {
    return ls.get("roleCount") || 0;
};

export { hasPermission };

export default {
    data() {
        return {
            globalOnlineUsers: [],
        };
    },

    computed: {
        // API & Auth
        apiHeaders() {
            return {
                "Content-Type": "application/json",
                Authorization: "Bearer " + this.secureLocalStorage("token"),
                Company: getSubCompanyId?.value,
            };
        },

        authUser() {
            return this.secureLocalStorage("user")
                ? JSON.parse(this.secureLocalStorage("user"))
                : null;
        },

        // Role checks
        isSuperAdmin() {
            if (!(this.authUser && this.authUser.roles)) {
                return false;
            }
            return this.authUser.roles.find((item) => item.id == 1);
        },

        $isSuperAdmin() {
            return (
                JSON.parse(this.secureLocalStorage("user"))?.roles?.some(
                    (role) => role.name === "super_admin"
                ) || false
            );
        },

        isAdmin() {
            if (!(this.authUser && this.authUser.roles)) {
                return false;
            }
            return this.authUser.roles.find((item) => item.id == 2);
        },

        // Module visibility
        socialPostVisible() {
            if (!(this.authUser && this.authUser.modules)) {
                return false;
            }
            let modules = JSON.parse(this.authUser.modules);
            return modules.find((item) => item == "wall");
        },

        $isGiftVisible() {
            if (!(this.authUser && this.authUser.modules)) {
                return false;
            }
            let modules = JSON.parse(this.authUser.modules);
            return modules.find((item) => item === "gift") !== undefined;
        },

        $isCatalogTrainingVisible() {
            if (!(this.authUser && this.authUser.modules)) {
                return false;
            }
            let modules = JSON.parse(this.authUser.modules);
            return modules.find((item) => item == "education-category");
        },

        CatalogTrainingVisible() {
            if (!(this.authUser && this.authUser.modules)) {
                return false;
            }
            let modules = JSON.parse(this.authUser.modules);
            return modules.find((item) => item == "education-category");
        },

        CollectionVisible() {
            if (!(this.authUser && this.authUser.modules)) {
                return false;
            }
            let modules = JSON.parse(this.authUser.modules);
            return modules.find((item) => item == "my-collection");
        },
        dashboardMenuVersion() {
            return dashboardMenuSyncState.version;
        },
    },

    methods: {
        // Storage
        secureLocalStorage(key = null) {
            return key ? ls.get(key) : ls.getAllKeys();
        },

        user() {
            return JSON.parse(this.secureLocalStorage("user"));
        },

        // String utilities
        turkishToLower(string) {
            const letters = { İ: "i", I: "ı", Ş: "ş", Ğ: "ğ", Ü: "ü", Ö: "ö", Ç: "ç" };
            string = string.replace(/(([İIŞĞÜÇÖ]))/g, (letter) => letters[letter]);
            return string.toLowerCase();
        },

        capitalize(string) {
            let pieces = string.split(" ");
            let result = "";
            for (let i = 0; i < pieces.length; i++) {
                result += pieces[i].charAt(0).toUpperCase() + pieces[i].slice(1).toLowerCase();
                if (pieces.length - 1 !== i) {
                    result += " ";
                }
            }
            return result;
        },

        // Form & Data utilities
        createFormDataForList(formData, key, data) {
            if (data === Object(data) || Array.isArray(data)) {
                for (let i in data) {
                    if (i.toString() === "file_names") {
                        continue;
                    }
                    if (data.file_names !== undefined) {
                        if (data.file_names.includes(i)) {
                            formData.append(key + "[" + i + "]", data[i]);
                            continue;
                        }
                    }
                    this.createFormDataForList(formData, key + "[" + i + "]", data[i]);
                }
            } else {
                formData.append(key, data);
            }
        },

        bytesToSize(bytes) {
            const sizes = ["Bytes", "KB", "MB", "GB", "TB"];
            if (bytes == 0) return "0 Byte";
            const i = parseInt(Math.floor(Math.log(bytes) / Math.log(1024)));
            return Math.round(bytes / Math.pow(1024, i), 2) + " " + sizes[i];
        },

        getDashboardMenuItems(fallbackMenu = []) {
            // Reactive dependency for menu cache refresh after widget/language updates
            void this.dashboardMenuVersion;
            const cachedMenu = this.secureLocalStorage("dashboardMenuList")?.menu;
            if (Array.isArray(cachedMenu) && cachedMenu.length) {
                return cachedMenu;
            }
            return Array.isArray(fallbackMenu) ? fallbackMenu : [];
        },

        getLocalizedWidgetTitle(widget, fallback = "", localeOverride = null) {
            if (!widget) {
                return fallback;
            }

            const activeLanguageCode = String(
                localeOverride || (this.$i18n && this.$i18n.locale) || this.secureLocalStorage("language") || "tr"
            ).toLowerCase();
            const normalizedLanguageCode = activeLanguageCode.replace("_", "-");
            const baseLanguageCode = normalizedLanguageCode.split("-")[0];

            const langTitle = widget.lang_title && typeof widget.lang_title === "object"
                ? widget.lang_title
                : null;

            if (langTitle) {
                const localized =
                    langTitle[activeLanguageCode] ??
                    langTitle[normalizedLanguageCode] ??
                    langTitle[baseLanguageCode] ??
                    langTitle[activeLanguageCode.toUpperCase()] ??
                    langTitle[baseLanguageCode.toUpperCase()];

                if (localized !== undefined && localized !== null && String(localized).trim() !== "") {
                    return String(localized);
                }
            }

            if (widget.title && String(widget.title).trim() !== "") {
                return String(widget.title);
            }

            if (widget.type_lang && String(widget.type_lang).trim() !== "") {
                return String(widget.type_lang);
            }

            const normalizedFallback =
                fallback !== undefined &&
                fallback !== null &&
                String(fallback).trim() !== ""
                    ? String(fallback)
                    : "";

            return normalizedFallback;
        },

        timeZone() {
            // timezona dile göre ayarlanıyor
            const language = this.secureLocalStorage("language");

            const languageTimeZoneMap = {
                tr: "Europe/Istanbul",   // Türkiye
                de: "Europe/Berlin",     // Almanya
                ru: "Europe/Moscow",     // Rusya
                en: "America/New_York",  // İngilizce → varsayılan (örnek)
                az: "Asia/Baku",         // Azerbaycan
            };

            return languageTimeZoneMap[language] ||"Europe/Istanbul";
        }
    },

};
